import { Button } from "@cleanbudget/ui/components/button";
import { ToastProvider } from "@cleanbudget/ui/components/toast";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, type ErrorComponentProps, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ButtonLink } from "@/components/button-link";

import "../styles.css";

export interface RouterContext {
	readonly queryClient: QueryClient;
}

/**
 * The root: no frame of its own, only the toast stack every page can add to (`useToast`). Admin
 * pages sit under the `_admin` layout (sidebar, and a session required); `/login` and `/enrol` stand
 * alone.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
	component: Root,
	notFoundComponent: NotFound,
	errorComponent: ErrorPage,
});

function Root() {
	const { t } = useTranslation();
	return (
		<ToastProvider closeLabel={t("close")}>
			<Outlet />
		</ToastProvider>
	);
}

function NotFound() {
	const { t } = useTranslation();
	return (
		<main className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-3 px-4">
			<h1 className="font-heading text-xl font-semibold">{t("notFound.title")}</h1>
			<p className="text-muted-foreground">{t("notFound.description")}</p>
			<ButtonLink to="/" variant="outline" className="w-fit">
				{t("notFound.back")}
			</ButtonLink>
		</main>
	);
}

function ErrorPage({ error, reset }: ErrorComponentProps) {
	const { t } = useTranslation();
	return (
		<main className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-3 px-4">
			<h1 className="font-heading text-xl font-semibold">{t("error.title")}</h1>
			<p className="text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
			<Button variant="outline" className="w-fit" onClick={reset}>
				{t("error.retry")}
			</Button>
		</main>
	);
}
